<template>
  <transition name="slide-up">
    <div 
      v-if="shouldShowWidget"
      class="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
    >
      <div 
        class="compare-widget bg-gray-800 border-t border-gray-700 shadow-lg p-3 md:p-4 mb-0 w-full max-w-6xl mx-auto"
      >
        <div class="relative flex items-center justify-between">
          <!-- Title and Count -->
          <div class="flex items-center gap-2">
            <div class="bg-blue-500/20 p-1.5 rounded-full">
              <Icon icon="heroicons:square-2-stack" class="w-5 h-5 text-blue-400" />
            </div>
            <h3 class="text-white font-medium text-sm md:text-base">
              Compare Tools
              <span class="text-gray-400 ml-1">({{ compareCount }}/{{ maxTools }})</span>
            </h3>
          </div>
          
          <!-- Action Buttons -->
          <div class="flex items-center gap-2">
            <button 
              @click="clearTools"
              class="text-xs md:text-sm text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700"
              :disabled="compareCount === 0"
            >
              Clear All
            </button>
            
            <button 
              @click="goToCompare"
              class="text-xs md:text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded flex items-center gap-1"
              :disabled="compareCount < 2"
              :class="{ 'opacity-50 cursor-not-allowed': compareCount < 2 }"
            >
              <span>Compare</span>
              <Icon icon="heroicons:arrow-right" class="w-4 h-4" />
            </button>
            
            <button 
              @click="toggleWidget"
              class="p-1 text-gray-400 hover:text-white rounded hover:bg-gray-700"
              aria-label="Toggle compare widget"
            >
              <Icon 
                :icon="isWidgetExpanded ? 'heroicons:chevron-down' : 'heroicons:chevron-up'" 
                class="w-5 h-5" 
              />
            </button>
          </div>
        </div>
        
        <!-- Tool Cards -->
        <transition name="fade">
          <div 
            v-if="isWidgetExpanded"
            class="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mt-4"
          >
            <!-- Active Tool Cards -->
            <div 
              v-for="tool in compareTools" 
              :key="tool.id"
              class="bg-gray-700/50 rounded-lg p-2 relative flex flex-col items-center"
            >
              <button 
                @click="removeTool(tool.id)"
                class="absolute top-1 right-1 text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-600/50"
                aria-label="Remove from comparison"
              >
                <Icon icon="heroicons:x-mark" class="w-4 h-4" />
              </button>
              
              <img 
                :src="tool.image" 
                :alt="`${tool.name} logo`" 
                class="w-8 h-8 md:w-10 md:h-10 object-contain mb-1" 
                @error="onImageError"
              />
              
              <span class="text-xs text-center text-white line-clamp-2 mt-1">
                {{ tool.name }}
              </span>
            </div>
            
            <!-- Empty Slots -->
            <div 
              v-for="i in emptySlots" 
              :key="`empty-${i}`"
              class="bg-gray-700/20 rounded-lg p-2 border border-dashed border-gray-600 flex flex-col items-center justify-center"
            >
              <div class="text-gray-500 text-center">
                <Icon icon="heroicons:plus" class="w-6 h-6 mx-auto mb-1" />
                <span class="text-xs">Add Tool</span>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useCompareStore } from '@/store/compareModule';
import { useRouter } from '@/utils/vue-inertia-helpers';

// Store access
const compareStore = useCompareStore();
const router = useRouter();

// Computed properties from store
const compareTools = computed(() => compareStore.compareTools);
const compareCount = computed(() => compareStore.compareCount);
const shouldShowWidget = computed(() => compareStore.shouldShowWidget);
const maxTools = computed(() => compareStore.maxTools);
const isWidgetExpanded = computed(() => compareStore.widgetExpanded);

// Calculate empty slots
const emptySlots = computed(() => {
  return Math.max(0, maxTools.value - compareCount.value);
});

// Methods
const toggleWidget = () => {
  compareStore.toggleWidgetExpanded();
};

const clearTools = () => {
  compareStore.clearTools();
};

const removeTool = (toolId) => {
  compareStore.removeTool(toolId);
};

const goToCompare = () => {
  if (compareCount.value >= 2) {
    // Set flag that we're going to the compare page
    compareStore.setComparePage(true);
    
    // Navigate to compare page with tool IDs
    router.get('/compare', { 
      tools: compareStore.toolIds 
    });
  }
};

// Handle image loading errors
const onImageError = (event) => {
  event.target.src = '/storage/no-logo.png';
};
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, max-height 0.3s ease;
  overflow: hidden;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}

.compare-widget {
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>